<template>
	<!-- Perhaps a little dirty to teleport things around, but in this case it feels like an acceptable trade-off. -->
	<Teleport to="head">
		<link rel="icon" href="/app-icons/default/apple-touch-icon_120.png" />
		<link rel="apple-touch-icon" sizes="120x120" href="/app-icons/default/apple-touch-icon_120.png" />
		<link rel="apple-touch-icon" sizes="152x152" href="/app-icons/default/apple-touch-icon_152.png" />
		<link rel="apple-touch-icon" sizes="167x167" href="/app-icons/default/apple-touch-icon_167.png" />
		<link rel="apple-touch-icon" sizes="180x180" href="/app-icons/default/apple-touch-icon_180.png" />
		<meta name="msapplication-TileImage" content="/app-icons/default/apple-touch-icon_152.png" />
	</Teleport>

	<TooltipProvider :delayDuration="300">
		<div class="h-dvh flex overflow-hidden bg-base">
			<TheSidebar v-if="currentOrganisation" />

			<div class="flex flex-col flex-1 w-0 overflow-hidden">
				<div class="relative flex-shrink-0 flex h-16 bg-base shadow header-navigation print:hidden" :class="[defaultPadding ? 'mb-1' : '']">
					<div class="flex-1 px-5 flex justify-between items-center ml-14 lg:ml-0">
						<div class="flex-1 flex">
							<h1 class="text-2xl pl-2 font-semibold text-base-content capitalize">
								<slot name="pageheader">{{ title }}</slot>
							</h1>
						</div>

						<div v-if="me?.impersonating" class="flex items-center py-2 px-3 bg-red-500 text-sm impersonating-warning print:hidden rounded-md mr-2">
							<span class="flex p-1 rounded-md bg-red-800">
								<svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
								</svg>
							</span>

							<p class="ml-3 font-medium text-white truncate">
								<span class="text-white">
									{{ $t("Representation mode") }}
								</span>
								<a href="javascript:void(0)" @click="stopImpersonating" class="inline-block ml-2 font-extrabold underline hover:no-underline">{{ $t("Leave") }}</a>
							</p>
						</div>

						<!-- TRIAL NOTICE -->
						<div v-if="currentOrganisation?.trialAboutToExpire" class="hidden md:flex items-center py-2 px-3 alert alert-warning w-auto text-sm print:hidden">
							<ExclamationTriangleIcon class="w-5 h-5 text-yellow-800" />
							<p v-if="me?.hasAdminPermissions()">
								{{ $t('Your trial will expire in {count} days.', trialDaysLeft, { count: trialDaysLeft }) }}
								<router-link to="settings" class="underline hover:no-underline font-bold">{{ $t('More info') }}</router-link>
							</p>
							<p v-else>
								{{ $t('Your trial will expire in {count} days.', trialDaysLeft, { count: trialDaysLeft }) }}
							</p>
						</div>

						<div v-if="me" class="ml-4 flex items-center md:ml-6">
							<div class="flex items-center justify-center gap-2 px-2 mt-2">
								<SupportHubButton v-if="me.locale?.split(/[-_]/)[0].toLowerCase() === 'nl'" @openChat="showAgentChat = true" />
								<div data-closes-chat>
									<!-- Product Fruits is not booted while impersonating, so the bell would do nothing -->
									<div class="relative" id="newsfeed-btn-wrapper" :title="me.impersonating ? 'Announcements are not available while impersonating a user' : undefined">
										<span v-if="announcementsCount > 0" class="absolute left-3 -top-1 z-10 indicator-item badge bg-error-content indicator-center scale-75 border-none text-white">{{ announcementsCount }}</span>
										<button :ref="setAnnouncementsTrigger" id="announcements-btn" class="btn btn-ghost btn-circle btn-sm border-none" :class="{ 'text-gray-400 cursor-not-allowed': me.impersonating }" :aria-disabled="me.impersonating || undefined">
											<BellIcon class="w-7 h-7 shrink-0" />
										</button>
									</div>
								</div>
								<AgentChat v-model:open="showAgentChat" />
							</div>

							<!-- Vertical divider -->
							<div class="w-px h-6 bg-gray-300 shrink-0" />

							<div data-closes-chat>
								<TheUserMenu :user="me" />
							</div>
						</div>
					</div>
				</div>

				<main class="flex-1 relative focus:outline-none" :class="[
					scrollable ? 'pb-4 overflow-y-auto' : 'min-h-0 h-full overflow-hidden',
					defaultPadding ? 'px-4' : ''
				]
					">
					<slot />
				</main>
			</div>
		</div>
	</TooltipProvider>

	<TheNotifier />
</template>

<style>
@media print {
	@page {
		margin: 0;
	}

	body {
		margin: 1cm;
	}

	* {
		overflow: visible !important;
	}
}
</style>

<script lang="ts">
import { useUsersStore } from "@/stores/Users.store";
import { mapActions, mapState } from "pinia";

import TheSidebar from "./TheSideBar.vue";
import TheUserMenu from "./TheUserMenu.vue";
import TheNotifier from "@/components/TheNotifier.vue";
import SupportHubButton from "@/components/SupportHubButton.vue";
import AgentChat from "@/components/AgentChat.vue";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { DateTime } from "luxon";
import { ExclamationTriangleIcon, BellIcon } from "@heroicons/vue/24/outline";
import { TooltipProvider } from 'reka-ui';
import { announcementsCount, initializeProductFruits, setAnnouncementsTrigger } from "@/utils/ProductFruits.util";

export default {
	name: "TheStdLayout",

	props: {
		title: String,
		scrollable: {
			type: Boolean,
			default: true,
		},
		defaultPadding: {
			type: Boolean,
			default: true,
		},
	},

	data() {
		return {
			showAgentChat: false,
		};
	},

	computed: {
		...mapState(useUsersStore, ["me"]),
		...mapState(useOrganisationsStore, ["currentOrganisation"]),

		announcementsCount() {
			return announcementsCount.value;
		},

		trialDaysLeft() {
			if (!this.currentOrganisation || !this.currentOrganisation.termination_date) {
				return 0;
			}

			return Math.ceil(this.currentOrganisation.termination_date.diff(DateTime.now(), 'days').days);
		}
	},

	methods: {
		...mapActions(useUsersStore, ["stopImpersonating"]),

		setAnnouncementsTrigger,
	},

	components: {
		TheSidebar,
		TheUserMenu,
		TheNotifier,
		ExclamationTriangleIcon,
		BellIcon,
		TooltipProvider,
		SupportHubButton,
		AgentChat,
	},

	// This is a bit of a hack to prevent the main page 'elastic bounce' when the user scrolls to the top or bottom of the page and keeps trying
	// to scroll further. We add overflow-hidden to the html element to prevent this, and then re-enable it when the component is unmounted.
	// Reason why it's not added directly to the stylesheet is because other layouts (like TheBoardLayout!!) don't want this behavior.
	mounted() {
		document.documentElement.classList.add('overflow-hidden');

		// Product Fruits is booted by the standard layout rather than by App.vue, so pages without this layout
		// (activity board, reflection board, print views) never get tours or popups injected (#750). The router
		// guard has already loaded the user and organisation before any layout page renders, and the call is a
		// no-op once booted, so repeating it on every layout mount is safe and covers the organisation-select
		// screen, where the organisation is still missing.
		initializeProductFruits(this.me, this.currentOrganisation, this.$i18n.locale);
	},

	unmounted() {
		document.documentElement.classList.remove('overflow-hidden');
	},
}
</script>
